import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { Button } from "reactstrap";
import { auth } from "../app/firebase.config";
import {
  fetchCanvasCourses,
  postCanvasAssignmentFromPlan,
} from "../utils/lessonPlanApi";
import TLLogoLoader from "./TLLogoLoader";

const formatLessonBlueprintPrompt = (
  lessonBlueprint,
  lessonBlueprintAuthoritative = false,
) => {
  const blueprint =
    lessonBlueprint && typeof lessonBlueprint === "object"
      ? lessonBlueprint
      : null;
  if (!blueprint) return "";

  const objective = String(blueprint.objective || "").trim();
  const assessment = String(blueprint.assessment || "").trim();
  const keyPoints = Array.isArray(blueprint.keyPoints)
    ? blueprint.keyPoints
        .map((item) => String(item || "").trim())
        .filter(Boolean)
    : [];
  const lessonArc = Array.isArray(blueprint.lessonArc)
    ? blueprint.lessonArc
        .map((item) => String(item || "").trim())
        .filter(Boolean)
    : [];
  const lines = [];

  if (objective) lines.push(`- Objective: ${objective}`);
  if (keyPoints.length > 0)
    lines.push(`- Key points: ${keyPoints.join(" | ")}`);
  if (assessment) lines.push(`- Assessment: ${assessment}`);
  if (lessonArc.length > 0)
    lines.push(`- Lesson arc: ${lessonArc.join(" | ")}`);

  if (lines.length === 0) return "";

  if (lessonBlueprintAuthoritative) {
    return (
      `Teacher-approved lesson blueprint:\n${lines.join("\n")}\n` +
      "Use this blueprint as the authoritative structure for the lesson plan. Preserve its objective, assessment, key points, and lesson arc unless the user explicitly asks to change them."
    );
  }

  return (
    `Current lesson blueprint draft:\n${lines.join("\n")}\n` +
    "Use this draft as guidance, but tighten scope and improve specificity if the draft is repetitive or too broad."
  );
};

const LessonPlanDisplay = ({
  response,
  courses,
  onCourseClick,
  lessonBlueprint = null,
  lessonBlueprintAuthoritative = false,
  warmUpQuestion,
  includeWarmUp = true,
  essentialQuestions = [],
  enduringUnderstandings = [],
  includeEssentials = true,
  unitPlanText = "",
  unitPlanLoading = false,
  unitPlanError = "",
  materialsContent = "",
  videoLinks = [],
  showVideos = false,
  lessonPlanDriveLink = null,
  lessonPlansFolderLink = null,
  onOpenLessonPlansFolder,
  onUpdateResponse,
  onStartNewLessonPlan,
  showDashboardLink = false,
}) => {
  const [copied, setCopied] = React.useState(false);
  const [sections, setSections] = useState([]);
  const [rebalanceLoading, setRebalanceLoading] = useState(false);
  const [rebalanceError, setRebalanceError] = useState("");
  const [chatDraft, setChatDraft] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [hasUncommittedChanges, setHasUncommittedChanges] = useState(false);
  const [changedSectionTitles, setChangedSectionTitles] = useState(new Set());
  const [videoThumbFailures, setVideoThumbFailures] = useState({});
  const [lessonPlanVideo, setLessonPlanVideo] = useState(null);
  const [canvasCourses, setCanvasCourses] = useState([]);
  const [canvasLoading, setCanvasLoading] = useState(false);
  const [canvasError, setCanvasError] = useState("");
  const [canvasPostState, setCanvasPostState] = useState({});
  const planEndRef = useRef(null);
  const lessonPlanModalCloseRef = useRef(null);
  const autoOpenedRef = useRef(false);
  const lastResponseRef = useRef(response);
  const localUpdateRef = useRef(false);
  const debounceTimerRef = useRef(null);
  const sectionRefsMap = useRef(new Map());
  const storageKey = "teachleague_plan_sections_draft";
  const lessonPlanDriveHref = lessonPlanDriveLink || null;
  const lessonPlanFolderHref = lessonPlansFolderLink || null;
  const lessonPlanDriveLabel = "View Lesson Plan in Drive";
  const lessonPlanFolderLabel = "Open Teach League Lesson Plans folder";
  const hasFolderAction = Boolean(
    lessonPlanFolderHref || onOpenLessonPlansFolder,
  );
  const [isSignedIn, setIsSignedIn] = useState(Boolean(auth.currentUser));
  const lessonBlueprintPrompt = useMemo(
    () =>
      formatLessonBlueprintPrompt(
        lessonBlueprint,
        lessonBlueprintAuthoritative,
      ),
    [lessonBlueprint, lessonBlueprintAuthoritative],
  );

  const getSignature = (text = "") => {
    let hash = 5381;
    for (let i = 0; i < text.length; i += 1) {
      hash = (hash * 33) ^ text.charCodeAt(i);
    }
    return `sig_${hash >>> 0}_${text.length}`;
  };

  const getYouTubeThumbs = (url = "") => {
    const videoMatch =
      url.match(/[?&]v=([A-Za-z0-9_-]+)/) ||
      url.match(/youtu\.be\/([A-Za-z0-9_-]+)/);
    if (videoMatch) {
      const videoId = videoMatch[1];
      return [
        `https://i.ytimg.com/vi_webp/${videoId}/hqdefault.webp`,
        `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
        `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
        `https://img.youtube.com/vi/${videoId}/default.jpg`,
      ];
    }
    const listMatch = url.match(/[?&]list=([A-Za-z0-9_-]+)/);
    if (listMatch) {
      const playlistId = listMatch[1];
      return [
        `https://i.ytimg.com/vi_webp/videoseries/hqdefault.webp?list=${playlistId}`,
        `https://i.ytimg.com/vi/videoseries/hqdefault.jpg?list=${playlistId}`,
        `https://i.ytimg.com/vi/videoseries/mqdefault.jpg?list=${playlistId}`,
        `https://i.ytimg.com/vi/videoseries/default.jpg?list=${playlistId}`,
      ];
    }
    return [];
  };

  const getYouTubeTarget = (url = "") => {
    const videoMatch =
      url.match(/[?&]v=([A-Za-z0-9_-]+)/) ||
      url.match(/youtu\.be\/([A-Za-z0-9_-]+)/);
    const listMatch = url.match(/[?&]list=([A-Za-z0-9_-]+)/);
    const videoId = videoMatch ? videoMatch[1] : "";
    const playlistId = listMatch ? listMatch[1] : "";
    return {
      videoId,
      playlistId,
      isYouTube: Boolean(videoId || playlistId),
    };
  };

  const buildVideoLibraryLink = (url = "") => {
    const { videoId, playlistId, isYouTube } = getYouTubeTarget(url);
    if (!isYouTube) return null;
    const params = new URLSearchParams();
    if (playlistId) params.set("playlistId", playlistId);
    if (videoId) params.set("videoId", videoId);
    const query = params.toString();
    return `/videos${query ? `?${query}` : ""}`;
  };

  const buildYouTubeEmbedUrl = ({ videoId, playlistId }) => {
    if (videoId && playlistId) {
      return `https://www.youtube-nocookie.com/embed/${videoId}?list=${playlistId}`;
    }
    if (videoId) {
      return `https://www.youtube-nocookie.com/embed/${videoId}`;
    }
    if (playlistId) {
      return `https://www.youtube-nocookie.com/embed/videoseries?list=${playlistId}`;
    }
    return "";
  };

  const renderLessonPlanVideoCard = (item) => {
    const label = item.label || "Watch video";
    const url = item.url || "";
    const target = getYouTubeTarget(url);
    const libraryLink = buildVideoLibraryLink(url);
    const thumbOptions = getYouTubeThumbs(url);
    const thumbFailureCount = videoThumbFailures[url] || 0;
    const thumb = thumbOptions[thumbFailureCount] || "";
    const showThumb = Boolean(thumb);
    const key = `${label}-${url}`;

    const cardContent = (
      <div className="lesson-plan-video-card">
        {showThumb ? (
          <img
            src={thumb}
            alt={label}
            onError={() => handleVideoThumbError(url)}
            className="lesson-plan-video-thumb"
            loading="lazy"
          />
        ) : (
          <div className="lesson-plan-video-placeholder">
            Preview unavailable
          </div>
        )}
        <div className="lesson-plan-video-label">{label}</div>
      </div>
    );

    if (target.isYouTube) {
      return (
        <button
          className="lesson-plan-video-link"
          key={key}
          type="button"
          onClick={() =>
            setLessonPlanVideo({
              label,
              url,
              libraryLink,
              videoId: target.videoId,
              playlistId: target.playlistId,
            })
          }
        >
          {cardContent}
        </button>
      );
    }

    return (
      <a
        className="lesson-plan-video-link"
        key={key}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        {cardContent}
      </a>
    );
  };

  const isYouTubeUrl = (url = "") => /(?:youtube\.com|youtu\.be)/i.test(url);

  const extractYouTubeLinks = (text = "") => {
    const linkRegex =
      /(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\))|(https?:\/\/[^\s<]+)|(www\.[^\s<]+)/g;
    const links = [];
    const seen = new Set();
    let match;
    while ((match = linkRegex.exec(text)) !== null) {
      const label = match[2] || "YouTube Video";
      const rawUrl = match[3] || match[4] || match[5] || "";
      const url = rawUrl.startsWith("http") ? rawUrl : `https://${rawUrl}`;
      if (!isYouTubeUrl(url)) continue;
      if (seen.has(url)) continue;
      seen.add(url);
      links.push({ label, url });
    }
    return links;
  };

  const videoLinkSet = useMemo(() => {
    const set = new Set();
    videoLinks.forEach((item) => {
      if (item?.url) {
        set.add(item.url);
      }
    });
    return set;
  }, [videoLinks]);

  useEffect(() => {
    setVideoThumbFailures({});
  }, [videoLinks]);

  useEffect(() => {
    if (!lessonPlanVideo) return;
    const focusTimer = window.setTimeout(() => {
      lessonPlanModalCloseRef.current?.focus();
    }, 0);
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setLessonPlanVideo(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [lessonPlanVideo]);

  const handleVideoThumbError = (url) => {
    setVideoThumbFailures((prev) => ({
      ...prev,
      [url]: (prev[url] || 0) + 1,
    }));
  };

  const escapeHtml = (value = "") =>
    String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const formatContentHtml = (value = "") => {
    const text = String(value || "")
      .replace(/```[a-zA-Z0-9_-]*\n?/g, "")
      .replace(/```/g, "")
      .replace(/`/g, "");
    const linkRegex =
      /(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\))|(https?:\/\/[^\s<]+)|(www\.[^\s<]+)/g;
    let result = "";
    let lastIndex = 0;
    let match;
    while ((match = linkRegex.exec(text)) !== null) {
      result += escapeHtml(text.slice(lastIndex, match.index));
      if (match[2] && match[3]) {
        const label = match[2];
        const url = match[3];
        if (isYouTubeUrl(url)) {
          result += `<span class="lesson-plan-video-text" data-video-url="${escapeHtml(url)}">${escapeHtml(label)}</span>`;
        } else {
          result += `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer" style="color:#0ea5e9;text-decoration:underline;cursor:pointer;">${escapeHtml(label)}</a>`;
        }
      } else {
        const url = match[4] || match[5];
        const href = url.startsWith("http") ? url : `https://${url}`;
        if (isYouTubeUrl(href)) {
          result += `<span class="lesson-plan-video-text" data-video-url="${escapeHtml(href)}">${escapeHtml(url)}</span>`;
        } else {
          result += `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" style="color:#0ea5e9;text-decoration:underline;cursor:pointer;">${escapeHtml(url)}</a>`;
        }
      }
      lastIndex = match.index + match[0].length;
    }
    result += escapeHtml(text.slice(lastIndex));
    return result.replace(/\n/g, "<br />");
  };

  const formatEditableLink = (label, href) => {
    const safeLabel = (label || "").trim();
    const safeHref = (href || "").trim();
    if (!safeHref) return safeLabel;
    const hrefWithoutProtocol = safeHref.replace(/^https?:\/\//i, "");
    if (!safeLabel) return safeHref;
    if (safeLabel === safeHref) return safeHref;
    if (safeLabel === hrefWithoutProtocol) return safeLabel;
    return `[${safeLabel}](${safeHref})`;
  };

  const serializeEditableContent = (element) => {
    if (!element) return "";
    const blockTags = new Set([
      "DIV",
      "P",
      "LI",
      "UL",
      "OL",
      "SECTION",
      "ARTICLE",
      "HEADER",
      "FOOTER",
      "H1",
      "H2",
      "H3",
      "H4",
      "H5",
      "H6",
    ]);

    const serializeNode = (node) => {
      if (!node) return "";
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent || "";
      }
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return "";
      }
      const el = node;
      const tagName = el.tagName;
      if (tagName === "BR") return "\n";
      if (tagName === "A") {
        const href = el.getAttribute("href") || "";
        return formatEditableLink(el.textContent || "", href);
      }
      if (tagName === "SPAN" && el.dataset?.videoUrl) {
        return formatEditableLink(el.textContent || "", el.dataset.videoUrl);
      }
      const childText = Array.from(el.childNodes).map(serializeNode).join("");
      if (blockTags.has(tagName)) {
        return `${childText}\n`;
      }
      return childText;
    };

    const rawText = Array.from(element.childNodes).map(serializeNode).join("");
    return rawText
      .replace(/\u00a0/g, " ")
      .replace(/\r\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  const parseAssignmentBlockForCanvas = (block = "") => {
    const lines = block
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    if (!lines.length) {
      return { title: "", description: "" };
    }
    const titleLine = lines.find((line) => /^title\s*:/i.test(line));
    let title = titleLine ? titleLine.replace(/^title\s*:/i, "").trim() : "";
    let description = block.trim();
    const descriptionIndex = lines.findIndex((line) =>
      /^description\s*:/i.test(line),
    );
    if (descriptionIndex >= 0) {
      description = lines
        .slice(descriptionIndex)
        .join("\n")
        .replace(/^description\s*:/i, "")
        .trim();
    } else if (titleLine) {
      description = lines
        .filter((line) => line !== titleLine)
        .join("\n")
        .trim();
    }
    if (!title) {
      title = lines[0].replace(/^assignment\s*:/i, "").trim();
    }
    if (!description) {
      description = block.trim();
    }
    return { title, description };
  };

  const extractCanvasLinkFromBlock = (block = "") => {
    const match = block.match(/Canvas\s*link\s*:\s*(https?:\/\/\S+)/i);
    return match ? match[1] : "";
  };

  const buildCanvasLinkLines = (url, courseName) => {
    const lines = [];
    if (courseName) {
      lines.push(`Canvas course: ${courseName}`);
    }
    lines.push(`Canvas link: ${url}`);
    return lines.join("\n");
  };

  const getCanvasStateKey = (sectionId, index) =>
    `${sectionId}-canvas-${index}`;

  const updateCanvasState = (key, updates) => {
    setCanvasPostState((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...updates,
      },
    }));
  };

  const getCanvasState = (key) =>
    canvasPostState[key] || {
      courseId: "",
      dueDate: "",
      pointsPossible: "",
      publish: false,
      posting: false,
      message: "",
      tone: "info",
    };

  const formatCanvasCourseLabel = (course) => {
    if (!course) return "Canvas course";
    const name =
      course.name ||
      course.course_code ||
      course.courseCode ||
      `Course ${course.id}`;
    const code = course.courseCode || course.course_code;
    const termName = course.term?.name;
    let label = code ? `${name} (${code})` : name;
    if (termName) {
      label = `${label} - ${termName}`;
    }
    return label;
  };

  const loadCanvasCourses = async () => {
    setCanvasLoading(true);
    setCanvasError("");
    try {
      const data = await fetchCanvasCourses();
      const courses = Array.isArray(data?.courses) ? data.courses : [];
      setCanvasCourses(courses);
    } catch (error) {
      setCanvasError(error.message || "Canvas courses unavailable.");
    } finally {
      setCanvasLoading(false);
    }
  };

  const formatUnitPlanHtml = (value = "") => {
    const lines = value.split("\n");
    const labels = [
      "Name of previous unit",
      "Name of next unit",
      "Currently in",
      "Previous Unit",
      "Current Unit",
      "Next Unit",
    ];
    const unitLabels = labels.map((label) => label.toLowerCase());
    const labelRegex = new RegExp(`^\\s*(${labels.join("|")})\\s*:`, "i");
    const formatted = lines
      .map((line) => {
        const match = line.match(labelRegex);
        if (!match) return escapeHtml(line);
        const label = match[1];
        const rest = line.slice(match[0].length).trim();
        const normalizedRest = rest.toLowerCase();
        const normalizedLabel = label.toLowerCase();
        const isUnitLabel = unitLabels.some((item) =>
          normalizedLabel.includes(item),
        );
        const isMissingUnitValue =
          !rest ||
          ["n/a", "na", "none"].includes(normalizedRest) ||
          unitLabels.some((placeholder) =>
            normalizedRest.includes(placeholder),
          );
        if (isUnitLabel && isMissingUnitValue) {
          return null;
        }
        if (!rest) {
          return null;
        }
        return `<strong>${escapeHtml(label)}:</strong> ${escapeHtml(rest)}`.trim();
      })
      .filter(Boolean);
    return formatted.join("<br />");
  };

  useEffect(() => {
    if (!planEndRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !autoOpenedRef.current) {
          autoOpenedRef.current = true;
          setChatOpen(true);
        }
      },
      { threshold: 0.8 },
    );
    observer.observe(planEndRef.current);
    return () => {
      observer.disconnect();
      // Clean up debounce timer on unmount
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsSignedIn(Boolean(user));
    });
    return () => unsubscribe();
  }, []);

  const parseAssignmentBlocks = (value = "") =>
    value
      .split(/\n\s*\n/)
      .map((block) => block.trim())
      .filter(Boolean);
  const parsePlanSections = (text) => {
    if (!text) return [];
    const lines = text.split("\n");
    const parsed = [];
    let current = { title: "Lesson Plan", content: [] };
    const headingRegex = /^##+\s+(.*)/;
    const isLessonPlanStub = (title, content) =>
      title.toLowerCase().trim() === "lesson plan" &&
      /^lesson\s*plan\s*:?$/i.test(content.trim());

    lines.forEach((line) => {
      const match = line.match(headingRegex);
      if (match) {
        const contentText = current.content.join("\n").trim();
        if (
          contentText.length > 0 &&
          !isLessonPlanStub(current.title, contentText)
        ) {
          parsed.push({
            title: current.title,
            content: contentText,
          });
        }
        current = { title: match[1].trim(), content: [] };
      } else {
        current.content.push(line);
      }
    });

    const finalContent = current.content.join("\n").trim();
    if (
      finalContent.length > 0 &&
      !isLessonPlanStub(current.title, finalContent)
    ) {
      parsed.push({
        title: current.title,
        content: finalContent,
      });
    }

    return parsed;
  };

  const buildSections = (responseText = response) => {
    const nextSections = [];
    const parsedSections = parsePlanSections(responseText);
    const existingHeadings = new Set(
      parsedSections.map((section) => section.title.toLowerCase().trim()),
    );

    if (
      includeEssentials &&
      essentialQuestions.length > 0 &&
      !existingHeadings.has("essential questions")
    ) {
      nextSections.push({
        id: "essential-questions",
        title: "Essential Questions",
        content: essentialQuestions.map((q) => `- ${q}`).join("\n"),
        originalContent: essentialQuestions.map((q) => `- ${q}`).join("\n"),
        included: true,
      });
    }

    if (
      includeEssentials &&
      enduringUnderstandings.length > 0 &&
      !existingHeadings.has("enduring understandings")
    ) {
      nextSections.push({
        id: "enduring-understandings",
        title: "Enduring Understandings",
        content: enduringUnderstandings.map((u) => `- ${u}`).join("\n"),
        originalContent: enduringUnderstandings.map((u) => `- ${u}`).join("\n"),
        included: true,
      });
    }

    if (
      includeWarmUp &&
      warmUpQuestion?.trim() &&
      !existingHeadings.has("warm-up question") &&
      !existingHeadings.has("warm up question")
    ) {
      nextSections.push({
        id: "warm-up",
        title: "Warm-Up Question",
        content: warmUpQuestion.trim(),
        originalContent: warmUpQuestion.trim(),
        included: true,
      });
    }

    parsedSections.forEach((section, index) => {
      const id = `plan-${index}-${section.title}`;
      nextSections.push({
        id,
        title: section.title || "Section",
        content: section.content,
        originalContent: section.content,
        included: true,
      });
    });

    if (materialsContent?.trim()) {
      const materialsHeading =
        existingHeadings.has("materials") ||
        existingHeadings.has("materials & references") ||
        existingHeadings.has("materials and references") ||
        existingHeadings.has("resources");
      if (!materialsHeading) {
        nextSections.push({
          id: "materials",
          title: "Materials & References",
          content: materialsContent.trim(),
          originalContent: materialsContent.trim(),
          included: true,
        });
      }
    }

    if (showVideos || videoLinks.length > 0) {
      const videoHeading =
        existingHeadings.has("videos") ||
        existingHeadings.has("video resources");
      if (!videoHeading) {
        const videoContent =
          videoLinks.length > 0
            ? videoLinks
                .map((item) => {
                  const label = item.label || "Watch video";
                  const url = item.url || "";
                  return `- [${label}](${url})`.trim();
                })
                .join("\n")
            : "No videos found yet. Add YouTube links to courses to populate this section.";
        nextSections.push({
          id: "videos",
          title: "Videos",
          content: videoContent,
          originalContent: videoContent,
          included: true,
        });
      }
    }

    if (unitPlanText?.trim() && !existingHeadings.has("unit plan")) {
      const unitLines = unitPlanText.trim().split("\n");
      const unitContent =
        unitLines[0]?.toLowerCase() === "unit plan"
          ? unitLines.slice(1).join("\n").trim()
          : unitPlanText.trim();
      nextSections.push({
        id: "unit-plan",
        title: "Unit Plan",
        content: unitContent,
        originalContent: unitContent,
        included: true,
      });
    }

    return nextSections;
  };

  const buildPlanText = useCallback(
    (sectionList) =>
      sectionList
        .filter((section) => section.included)
        .map((section) => `## ${section.title}\n${section.content}`.trim())
        .join("\n\n"),
    [],
  );

  const scrollToSection = (sectionTitle) => {
    // Find the section by title
    const sectionElement = sectionRefsMap.current.get(sectionTitle);
    if (sectionElement) {
      const isVaporWaveTheme = document.body.classList.contains("vaporWaveAlt");
      const highlightColor = isVaporWaveTheme
        ? "rgba(255, 98, 199, 0.24)"
        : "#fef3c7";
      const resetColor = isVaporWaveTheme ? "rgba(20, 10, 46, 0.9)" : "#ffffff";
      sectionElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      // Add a brief highlight effect
      sectionElement.style.transition = "background-color 0.3s ease";
      sectionElement.style.backgroundColor = highlightColor;
      setTimeout(() => {
        sectionElement.style.backgroundColor = resetColor;
      }, 1500);
    }
  };

  useEffect(() => {
    const signature = getSignature(response);
    const storedRaw = localStorage.getItem(storageKey);
    let stored = null;
    try {
      stored = storedRaw ? JSON.parse(storedRaw) : null;
    } catch (error) {
      stored = null;
    }

    if (response !== lastResponseRef.current && localUpdateRef.current) {
      localUpdateRef.current = false;
      lastResponseRef.current = response;
      return;
    }

    let nextSections = buildSections(response);

    if (stored?.signature === signature && Array.isArray(stored.sections)) {
      nextSections = nextSections.map((section) => {
        const match = stored.sections.find((item) => item.id === section.id);
        if (!match) return section;
        return {
          ...section,
          content:
            typeof match.content === "string" ? match.content : section.content,
          included:
            typeof match.included === "boolean"
              ? match.included
              : section.included,
        };
      });
    }

    if (!includeEssentials) {
      nextSections = nextSections.map((section) =>
        section.id === "essential-questions" ||
        section.id === "enduring-understandings"
          ? { ...section, included: false }
          : section,
      );
    }

    if (!includeWarmUp) {
      nextSections = nextSections.map((section) =>
        section.id === "warm-up" ? { ...section, included: false } : section,
      );
    }

    setSections(nextSections);
    lastResponseRef.current = response;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    response,
    includeWarmUp,
    warmUpQuestion,
    includeEssentials,
    essentialQuestions,
    enduringUnderstandings,
    unitPlanText,
  ]);

  useEffect(() => {
    if (!sections.length) return;
    const signature = getSignature(response);
    const payload = { signature, sections };
    localStorage.setItem(storageKey, JSON.stringify(payload));
  }, [sections, response]);

  useEffect(() => {
    if (!localUpdateRef.current) return;
    onUpdateResponse?.(buildPlanText(sections));
  }, [buildPlanText, onUpdateResponse, sections]);

  useEffect(() => {
    if (!isSignedIn) return;
    loadCanvasCourses();
  }, [isSignedIn]);

  const handleToggleSection = (id) => {
    setSections((prev) => {
      const next = prev.map((section) =>
        section.id === id
          ? { ...section, included: !section.included }
          : section,
      );
      localUpdateRef.current = true;
      return next;
    });
  };

  const handleSectionChange = (id, value) => {
    let actuallyChanged = false;
    let updatedChangedSections = new Set();

    setSections((prev) => {
      const next = prev.map((section) => {
        if (section.id === id) {
          // Compare against ORIGINAL content to detect actual changes
          // Trim both values to ignore insignificant whitespace differences
          const originalTrimmed = (section.originalContent || "").trim();
          const newTrimmed = (value || "").trim();
          const hasChangedFromOriginal = originalTrimmed !== newTrimmed;

          if (hasChangedFromOriginal) {
            actuallyChanged = true;
            setChangedSectionTitles((prevTitles) => {
              const newTitles = new Set(prevTitles);
              newTitles.add(section.title);
              updatedChangedSections = newTitles;
              return newTitles;
            });
          } else {
            // If reverted back to original, remove from changed sections
            setChangedSectionTitles((prevTitles) => {
              const newTitles = new Set(prevTitles);
              newTitles.delete(section.title);
              updatedChangedSections = newTitles;
              return newTitles;
            });
          }

          return { ...section, content: value };
        }
        return section;
      });
      localUpdateRef.current = true;
      return next;
    });

    // Only debounce if there was an actual change from original
    if (actuallyChanged) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        setHasUncommittedChanges(true);
        setChatOpen(true); // Auto-open the panel to show the notification
      }, 2000);
    } else {
      // If no sections are changed anymore, clear the uncommitted changes flag
      if (updatedChangedSections.size === 0) {
        setHasUncommittedChanges(false);
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      }
    }
  };

  const handleUndoSection = (id) => {
    setSections((prev) => {
      const next = prev.map((section) => {
        if (section.id === id) {
          // Remove this section from changed sections when undone
          setChangedSectionTitles((prevTitles) => {
            const newTitles = new Set(prevTitles);
            newTitles.delete(section.title);
            return newTitles;
          });

          // If no more changed sections, clear uncommitted changes flag
          if (changedSectionTitles.size <= 1) {
            setHasUncommittedChanges(false);
          }

          return { ...section, content: section.originalContent };
        }
        return section;
      });
      localUpdateRef.current = true;
      return next;
    });
  };

  const handleRebalance = async (instructions) => {
    setRebalanceLoading(true);
    setRebalanceError("");
    try {
      const { getAIProviderManager } =
        await import("../utils/aiProviderFactory");
      const aiManager = getAIProviderManager();

      // Build a more comprehensive prompt that highlights changed sections
      const changedSectionsList = Array.from(changedSectionTitles).join(", ");
      const changeNotice =
        hasUncommittedChanges && changedSectionsList
          ? `\n\nIMPORTANT: The following sections have been manually edited and their changes should be preserved: ${changedSectionsList}. Regenerate ALL other sections to maintain coherence with these changes.`
          : "";
      const blueprintNotice = lessonBlueprintPrompt
        ? `\n\n${lessonBlueprintPrompt}`
        : "";

      const prompt = `Regenerate the entire lesson plan for complete coherence and alignment across all sections. Keep all section headings. Return a complete markdown lesson plan with headings.${changeNotice}${blueprintNotice}

Current lesson plan:
${buildPlanText(sections)}

Additional instructions:
${instructions || "Ensure all sections flow together naturally and support the learning objectives."}`;

      const result = await aiManager.generate(prompt);
      if (result?.content) {
        const nextResponse = result.content.trim();
        onUpdateResponse?.(nextResponse);
        setSections(buildSections(nextResponse));

        // Clear change tracking after successful update
        setHasUncommittedChanges(false);
        setChangedSectionTitles(new Set());
        setChatDraft("");
      }
    } catch (error) {
      console.error("Rebalance failed:", error);
      setRebalanceError("Unable to regenerate the plan right now.");
    } finally {
      setRebalanceLoading(false);
    }
  };

  const buildOutputText = () => buildPlanText(sections);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildOutputText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");

    // Convert markdown links to HTML for printing
    const htmlContent = buildOutputText().replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2">$1</a>',
    );

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Lesson Plan</title>
          <style>
            body {
              font-family: 'Century Gothic Paneuropean', 'Century Gothic', 'AppleGothic', 'URW Gothic L', 'Tw Cen MT', Arial, sans-serif;
              line-height: 1.6;
              color: #000;
              max-width: 8.5in;
              margin: 0 auto;
              padding: 1cm;
            }
            h1, h2, h3 { color: #000; margin-top: 1em; }
            a { color: var(--color-primary); text-decoration: underline; }
            @media print {
              body { margin: 1cm; }
              a { color: #000; }
            }
          </style>
        </head>
        <body>
          <pre style="white-space: pre-wrap; font-family: inherit;">${htmlContent}</pre>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <>
      <style>{`
        .tl-tooltip-wrap {
          position: relative;
          display: inline-flex;
          align-items: center;
        }
        .tl-tooltip-text {
          position: absolute;
          bottom: calc(100% + 8px);
          right: 0;
          background: #0f172a;
          color: #f8fafc;
          padding: 6px 8px;
          border-radius: 6px;
          font-size: 12px;
          white-space: nowrap;
          opacity: 0;
          pointer-events: none;
          transform: translateY(4px);
          transition: opacity 0.15s ease, transform 0.15s ease;
          z-index: 10;
        }
        .tl-tooltip-wrap:hover .tl-tooltip-text,
        .tl-tooltip-wrap:focus-within .tl-tooltip-text {
          opacity: 1;
          transform: translateY(0);
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.1);
          }
        }
      `}</style>
      <div
        className="d-flex flex-column flex-lg-row align-items-lg-start"
        style={{ gap: "0.85rem" }}
      >
        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <div className="lesson-plan-actions mb-3">
            <div className="lesson-plan-actions__links">
              {onStartNewLessonPlan && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={onStartNewLessonPlan}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onStartNewLessonPlan();
                    }
                  }}
                  className="lesson-planner-reset-btn d-inline-flex align-items-center"
                  style={{
                    cursor: "pointer",
                    color: "var(--color-primary)",
                    textDecoration: "none",
                    fontWeight: 500,
                    fontSize: "0.82rem",
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  ← Start New Lesson Plan
                </span>
              )}
              {showDashboardLink && (
                <Link
                  to="/dashboard"
                  className="lesson-plan-dashboard-link d-inline-flex align-items-center"
                >
                  Go to Dashboard
                </Link>
              )}
              {(lessonPlanDriveHref || hasFolderAction) && (
                <div className="lesson-plan-drive-container">
                  <span className="lesson-plan-drive-label">
                    <i className="fa fa-google" aria-hidden="true" />
                    Google Drive
                  </span>
                  {lessonPlanDriveHref && (
                    <a
                      href={lessonPlanDriveHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="lesson-plan-drive-link lesson-plan-drive-link--plan d-inline-flex align-items-center"
                    >
                      {lessonPlanDriveLabel}
                    </a>
                  )}
                  {lessonPlanFolderHref && (
                    <a
                      href={lessonPlanFolderHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="lesson-plan-drive-link lesson-plan-drive-link--folder d-inline-flex align-items-center"
                    >
                      {lessonPlanFolderLabel}
                    </a>
                  )}
                  {!lessonPlanFolderHref && hasFolderAction && (
                    <button
                      type="button"
                      onClick={onOpenLessonPlansFolder}
                      className="lesson-plan-drive-link lesson-plan-drive-link--folder d-inline-flex align-items-center"
                    >
                      {lessonPlanFolderLabel}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="lesson-plan-actions__buttons">
              <Button
                className="lesson-plan-action-btn lesson-plan-action-btn--print"
                size="sm"
                outline
                color="secondary"
                onClick={handlePrint}
                title="Print lesson plan"
              >
                <span className="me-1">🖨️</span>
                Print
              </Button>
              <Button
                className="lesson-plan-action-btn lesson-plan-action-btn--copy"
                size="sm"
                color={copied ? "success" : "primary"}
                onClick={handleCopy}
                title="Copy to clipboard"
              >
                {copied ? (
                  <>
                    <span className="me-1">✓</span>
                    Copied!
                  </>
                ) : (
                  <>
                    <span className="me-1">📋</span>
                    Copy
                  </>
                )}
              </Button>
            </div>
          </div>
          {/* {referencedCourses.length > 0 && (
            <div className="mb-3">
              <div className="text-muted small mb-2">
                Courses mentioned in this plan
              </div>
              <div className="d-flex flex-wrap gap-2">
                {referencedCourses.map((course) => (
                  <button
                    key={course.name}
                    type="button"
                    onClick={() => handleCourseSelect(course)}
                    style={{
                      border: "1px solid #e2e8f0",
                      background: "#f8fafc",
                      borderRadius: "999px",
                      padding: "0.3rem 0.7rem",
                      fontSize: "0.85rem",
                      cursor: "pointer",
                    }}
                  >
                    {course.name}
                  </button>
                ))}
              </div>
            </div>
          )} */}
          <div
            className="lesson-plan-content"
            style={{ fontSize: "1rem", lineHeight: "1.6" }}
          >
            {sections.map((section) => {
              const isEdited = section.content !== section.originalContent;
              if (!section.included) {
                return (
                  <div
                    key={section.id}
                    className="lesson-plan-section-card lesson-plan-section-card--hidden"
                    ref={(el) => {
                      if (el) {
                        sectionRefsMap.current.set(section.title, el);
                      }
                    }}
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: "12px",
                      padding: "0.65rem 1rem",
                      marginBottom: "1rem",
                      background: "#ffffff",
                      opacity: 0.5,
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div
                        className="lesson-plan-section-title"
                        style={{ fontWeight: 600 }}
                      >
                        {section.title}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleToggleSection(section.id)}
                        className="lesson-plan-section-icon-btn"
                        style={{
                          border: "none",
                          background: "transparent",
                          color: "var(--color-primary-dark)",
                          cursor: "pointer",
                        }}
                        aria-label="Show section"
                      >
                        <i className="fa fa-book" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                );
              }

              const isVideoSection = section.title
                ?.toLowerCase()
                .includes("video");
              const isUnitPlanSection = section.title
                ?.toLowerCase()
                .includes("unit plan");
              const isAssignmentsSection = section.title
                ?.toLowerCase()
                .includes("assignment");
              const assignmentBlocks = isAssignmentsSection
                ? parseAssignmentBlocks(section.content || "")
                : [];
              const inlineVideoLinks = extractYouTubeLinks(
                section.content || "",
              ).filter((item) => !videoLinkSet.has(item.url));

              return (
                <div
                  key={section.id}
                  className="lesson-plan-section-card"
                  ref={(el) => {
                    if (el) {
                      sectionRefsMap.current.set(section.title, el);
                    }
                  }}
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    padding: "0.85rem 1rem",
                    marginBottom: "1rem",
                    background: "#ffffff",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <div
                        className="lesson-plan-section-title"
                        style={{ fontWeight: 600 }}
                      >
                        {section.title}
                      </div>
                      {isAssignmentsSection && (
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="lesson-plan-section-badge"
                            style={{
                              fontSize: "0.75rem",
                              borderRadius: "999px",
                              padding: "0.1rem 0.5rem",
                              background: canvasError ? "#fef2f2" : "#eff6ff",
                              color: canvasError ? "#b91c1c" : "#1d4ed8",
                              border: canvasError
                                ? "1px solid #fecaca"
                                : "1px solid #bfdbfe",
                            }}
                          >
                            {canvasLoading
                              ? "Loading Canvas"
                              : !isSignedIn
                                ? "Sign in for Canvas"
                                : canvasError
                                  ? "Canvas not connected"
                                  : "Canvas ready"}
                          </span>
                          {(!isSignedIn || canvasError) && (
                            <Link
                              to="/mydata"
                              style={{
                                fontSize: "0.75rem",
                                color: "#1d4ed8",
                                textDecoration: "underline",
                                fontWeight: 600,
                              }}
                            >
                              Connect Canvas
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {isEdited && (
                        <button
                          type="button"
                          onClick={() => handleUndoSection(section.id)}
                          className="lesson-plan-section-icon-btn"
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "var(--color-primary-dark)",
                            cursor: "pointer",
                          }}
                          aria-label="Undo changes"
                        >
                          <i className="fa fa-undo" aria-hidden="true" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleToggleSection(section.id)}
                        className="lesson-plan-section-icon-btn"
                        style={{
                          border: "none",
                          background: "transparent",
                          color: "var(--color-primary-dark)",
                          cursor: "pointer",
                        }}
                        aria-label="Hide section"
                      >
                        <i className="fa fa-book-open" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  {!isAssignmentsSection && (
                    <div
                      className="lesson-plan-section-editor"
                      contentEditable
                      suppressContentEditableWarning
                      onClick={(event) => {
                        const target = event.target;
                        if (!(target instanceof HTMLElement)) return;
                        const link = target.closest("a");
                        if (link?.href) {
                          event.preventDefault();
                          event.stopPropagation();
                          window.open(
                            link.href,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }
                      }}
                      onBlur={(event) =>
                        handleSectionChange(
                          section.id,
                          serializeEditableContent(event.currentTarget),
                        )
                      }
                      role="textbox"
                      aria-multiline="true"
                      style={{
                        width: "100%",
                        borderRadius: "10px",
                        border: "1px solid #e2e8f0",
                        padding: "0.6rem 0.75rem",
                        fontSize: "0.95rem",
                        lineHeight: "1.5",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        outline: "none",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: isUnitPlanSection
                          ? formatUnitPlanHtml(section.content || "")
                          : formatContentHtml(section.content || ""),
                      }}
                    />
                  )}
                  {isAssignmentsSection && assignmentBlocks.length === 0 && (
                    <div
                      className="lesson-plan-section-editor"
                      contentEditable
                      suppressContentEditableWarning
                      onClick={(event) => {
                        const target = event.target;
                        if (!(target instanceof HTMLElement)) return;
                        const link = target.closest("a");
                        if (link?.href) {
                          event.preventDefault();
                          event.stopPropagation();
                          window.open(
                            link.href,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }
                      }}
                      onBlur={(event) =>
                        handleSectionChange(
                          section.id,
                          serializeEditableContent(event.currentTarget),
                        )
                      }
                      role="textbox"
                      aria-multiline="true"
                      style={{
                        width: "100%",
                        borderRadius: "10px",
                        border: "1px solid #e2e8f0",
                        padding: "0.6rem 0.75rem",
                        fontSize: "0.95rem",
                        lineHeight: "1.5",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        outline: "none",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: formatContentHtml(section.content || ""),
                      }}
                    />
                  )}
                  {inlineVideoLinks.length > 0 && (
                    <div className="lesson-plan-video-grid mt-3">
                      {inlineVideoLinks.map(renderLessonPlanVideoCard)}
                    </div>
                  )}
                  {isVideoSection && videoLinks.length > 0 && (
                    <div className="lesson-plan-video-grid mt-3">
                      {videoLinks.map(renderLessonPlanVideoCard)}
                    </div>
                  )}
                  {isAssignmentsSection && assignmentBlocks.length > 0 && (
                    <div
                      className="lesson-plan-assignment-list mt-3"
                      style={{
                        display: "block",
                      }}
                    >
                      <div className="d-flex flex-wrap gap-2">
                        {assignmentBlocks.map((block, index) => {
                          const blockKey = getCanvasStateKey(section.id, index);
                          const canvasState = getCanvasState(blockKey);
                          const existingCanvasLink =
                            extractCanvasLinkFromBlock(block);
                          const effectiveCourseId =
                            canvasState.courseId ||
                            (canvasCourses.length === 1
                              ? String(canvasCourses[0].id)
                              : "");
                          const handlePostToCanvas = async () => {
                            updateCanvasState(blockKey, {
                              message: "",
                              tone: "info",
                            });
                            if (!isSignedIn) {
                              updateCanvasState(blockKey, {
                                message:
                                  "Sign in to post assignments to Canvas.",
                                tone: "error",
                              });
                              return;
                            }
                            if (canvasError) {
                              updateCanvasState(blockKey, {
                                message: "Connect Canvas to post assignments.",
                                tone: "error",
                              });
                              return;
                            }
                            if (!effectiveCourseId) {
                              updateCanvasState(blockKey, {
                                message: "Select a Canvas course.",
                                tone: "error",
                              });
                              return;
                            }
                            const parsedPoints = canvasState.pointsPossible
                              ? Number(canvasState.pointsPossible)
                              : null;
                            if (
                              canvasState.pointsPossible &&
                              !Number.isFinite(parsedPoints)
                            ) {
                              updateCanvasState(blockKey, {
                                message: "Enter a valid points value.",
                                tone: "error",
                              });
                              return;
                            }
                            const { title, description } =
                              parseAssignmentBlockForCanvas(block);
                            if (!title && !description) {
                              updateCanvasState(blockKey, {
                                message:
                                  "Add a title or description before posting.",
                                tone: "error",
                              });
                              return;
                            }
                            const dueAt = canvasState.dueDate
                              ? new Date(
                                  `${canvasState.dueDate}T00:00:00`,
                                ).toISOString()
                              : null;
                            updateCanvasState(blockKey, { posting: true });
                            try {
                              const result = await postCanvasAssignmentFromPlan(
                                {
                                  title,
                                  description,
                                  canvasCourseId: effectiveCourseId,
                                  dueAt,
                                  pointsPossible: parsedPoints,
                                  publish: canvasState.publish,
                                },
                              );
                              const url = result?.canvasAssignment?.url || "";
                              if (url) {
                                const courseName =
                                  result?.canvasAssignment?.courseName || "";
                                const linkLines = buildCanvasLinkLines(
                                  url,
                                  courseName,
                                );
                                const trimmedBlock = block.trim();
                                const nextBlock = trimmedBlock
                                  ? `${trimmedBlock}\n\n${linkLines}`
                                  : linkLines;
                                const nextBlocks = [...assignmentBlocks];
                                nextBlocks[index] = nextBlock;
                                const nextContent = nextBlocks
                                  .join("\n\n")
                                  .trim();
                                handleSectionChange(section.id, nextContent);
                              }
                              updateCanvasState(blockKey, {
                                posting: false,
                                message: "Posted to Canvas.",
                                tone: "success",
                              });
                            } catch (error) {
                              updateCanvasState(blockKey, {
                                posting: false,
                                message:
                                  error.message || "Unable to post to Canvas.",
                                tone: "error",
                              });
                            }
                          };
                          return (
                            <div
                              className="lesson-plan-assignment-card"
                              key={`${section.id}-assignment-${index}`}
                              style={{
                                borderRadius: "16px",
                                border: "1px solid #e2e8f0",
                                background: "#f8fafc",
                                padding: "0.5rem 0.8rem",
                                fontSize: "0.9rem",
                                flex: "1 1 100%",
                              }}
                            >
                              <div
                                className="lesson-plan-assignment-editor"
                                contentEditable
                                suppressContentEditableWarning
                                onBlur={(event) => {
                                  const nextBlocks = [...assignmentBlocks];
                                  nextBlocks[index] = serializeEditableContent(
                                    event.currentTarget,
                                  ).trim();
                                  const nextContent = nextBlocks
                                    .join("\n\n")
                                    .trim();
                                  handleSectionChange(section.id, nextContent);
                                }}
                                role="textbox"
                                aria-multiline="true"
                                style={{
                                  outline: "none",
                                  whiteSpace: "pre-wrap",
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: formatContentHtml(block),
                                }}
                              />
                              {existingCanvasLink ? (
                                <div
                                  style={{
                                    marginTop: "0.5rem",
                                    fontSize: "0.8rem",
                                    color: "#1d4ed8",
                                  }}
                                >
                                  Posted to Canvas.{" "}
                                  <a
                                    href={existingCanvasLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ fontWeight: 600 }}
                                  >
                                    Open in Canvas
                                  </a>
                                </div>
                              ) : (
                                <div
                                  style={{
                                    marginTop: "0.6rem",
                                    display: "grid",
                                    gap: "0.4rem",
                                  }}
                                >
                                  {!isSignedIn && (
                                    <div
                                      style={{
                                        fontSize: "0.75rem",
                                        color: "#64748b",
                                      }}
                                    >
                                      Sign in to post this assignment to Canvas.
                                    </div>
                                  )}
                                  {isSignedIn && canvasLoading && (
                                    <div
                                      style={{
                                        fontSize: "0.75rem",
                                        color: "#64748b",
                                      }}
                                    >
                                      <TLLogoLoader
                                        label={null}
                                        size={16}
                                        layout="inline"
                                        className="me-1"
                                      />
                                      Loading Canvas courses...
                                    </div>
                                  )}
                                  {isSignedIn && canvasError && (
                                    <div
                                      style={{
                                        fontSize: "0.75rem",
                                        color: "#b91c1c",
                                      }}
                                    >
                                      {canvasError}{" "}
                                      <Link
                                        to="/mydata"
                                        style={{ textDecoration: "underline" }}
                                      >
                                        Connect Canvas
                                      </Link>
                                    </div>
                                  )}
                                  {isSignedIn &&
                                    !canvasLoading &&
                                    !canvasError &&
                                    canvasCourses.length === 0 && (
                                      <div
                                        style={{
                                          fontSize: "0.75rem",
                                          color: "#64748b",
                                        }}
                                      >
                                        No Canvas courses available.{" "}
                                        <Link
                                          to="/mydata"
                                          style={{
                                            textDecoration: "underline",
                                          }}
                                        >
                                          Connect Canvas
                                        </Link>
                                      </div>
                                    )}
                                  {isSignedIn &&
                                    !canvasError &&
                                    canvasCourses.length > 0 && (
                                      <>
                                        <select
                                          value={effectiveCourseId}
                                          onChange={(event) =>
                                            updateCanvasState(blockKey, {
                                              courseId: event.target.value,
                                            })
                                          }
                                          style={{
                                            borderRadius: "10px",
                                            border: "1px solid #e2e8f0",
                                            padding: "0.35rem 0.55rem",
                                            fontSize: "0.85rem",
                                          }}
                                        >
                                          <option value="">
                                            Select Canvas course
                                          </option>
                                          {canvasCourses.map((course) => (
                                            <option
                                              key={course.id}
                                              value={course.id}
                                            >
                                              {formatCanvasCourseLabel(course)}
                                            </option>
                                          ))}
                                        </select>
                                        <div
                                          style={{
                                            display: "flex",
                                            gap: "0.4rem",
                                          }}
                                        >
                                          <input
                                            type="date"
                                            value={canvasState.dueDate}
                                            onChange={(event) =>
                                              updateCanvasState(blockKey, {
                                                dueDate: event.target.value,
                                              })
                                            }
                                            style={{
                                              flex: 1,
                                              borderRadius: "10px",
                                              border: "1px solid #e2e8f0",
                                              padding: "0.35rem 0.55rem",
                                              fontSize: "0.85rem",
                                            }}
                                          />
                                          <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={canvasState.pointsPossible}
                                            onChange={(event) =>
                                              updateCanvasState(blockKey, {
                                                pointsPossible:
                                                  event.target.value,
                                              })
                                            }
                                            placeholder="Points"
                                            style={{
                                              width: "110px",
                                              borderRadius: "10px",
                                              border: "1px solid #e2e8f0",
                                              padding: "0.35rem 0.55rem",
                                              fontSize: "0.85rem",
                                            }}
                                          />
                                        </div>
                                        <label
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: "0.35rem",
                                            fontSize: "0.8rem",
                                            color: "#475569",
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={canvasState.publish}
                                            onChange={(event) =>
                                              updateCanvasState(blockKey, {
                                                publish: event.target.checked,
                                              })
                                            }
                                          />
                                          Publish immediately
                                        </label>
                                        <button
                                          type="button"
                                          onClick={handlePostToCanvas}
                                          disabled={
                                            canvasState.posting ||
                                            !effectiveCourseId
                                          }
                                          style={{
                                            border: "1px solid #1d4ed8",
                                            background: "transparent",
                                            color: "#1d4ed8",
                                            borderRadius: "999px",
                                            padding: "0.3rem 0.7rem",
                                            fontWeight: 600,
                                            cursor:
                                              canvasState.posting ||
                                              !effectiveCourseId
                                                ? "not-allowed"
                                                : "pointer",
                                            opacity:
                                              canvasState.posting ||
                                              !effectiveCourseId
                                                ? 0.6
                                                : 1,
                                          }}
                                        >
                                          {canvasState.posting
                                            ? "Posting..."
                                            : "Post to Canvas"}
                                        </button>
                                        {canvasState.message && (
                                          <div
                                            style={{
                                              fontSize: "0.75rem",
                                              color:
                                                canvasState.tone === "error"
                                                  ? "#b91c1c"
                                                  : canvasState.tone ===
                                                      "success"
                                                    ? "#047857"
                                                    : "#334155",
                                            }}
                                          >
                                            {canvasState.message}
                                          </div>
                                        )}
                                      </>
                                    )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {/* Premium assignments pane temporarily disabled */}
                    </div>
                  )}
                </div>
              );
            })}
            {lessonPlanVideo && (
              <div
                className="video-modal-backdrop"
                role="dialog"
                aria-modal="true"
                onClick={(event) => {
                  if (event.target === event.currentTarget) {
                    setLessonPlanVideo(null);
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setLessonPlanVideo(null);
                  }
                }}
              >
                <div
                  className="video-modal"
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="video-modal-header">
                    <div className="video-modal-title">
                      {lessonPlanVideo.label || "Video"}
                    </div>
                    <button
                      type="button"
                      className="video-modal-close"
                      onClick={() => setLessonPlanVideo(null)}
                      aria-label="Close video"
                      ref={lessonPlanModalCloseRef}
                    >
                      ×
                    </button>
                  </div>
                  <div className="video-modal-player">
                    <iframe
                      title={lessonPlanVideo.label || "YouTube video player"}
                      src={buildYouTubeEmbedUrl(lessonPlanVideo)}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <div className="video-modal-footer">
                    {lessonPlanVideo.libraryLink && (
                      <Link
                        to={lessonPlanVideo.libraryLink}
                        className="video-modal-link"
                      >
                        Open in Video Library
                      </Link>
                    )}
                    {lessonPlanVideo.url && (
                      <a
                        href={lessonPlanVideo.url}
                        target="_blank"
                        rel="noreferrer"
                        className="video-modal-link"
                      >
                        Open on YouTube
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
            {(unitPlanLoading || unitPlanError) && (
              <div className="text-muted small mb-2">
                {unitPlanLoading && (
                  <span className="d-inline-flex align-items-center">
                    <TLLogoLoader
                      label={null}
                      size={16}
                      layout="inline"
                      className="me-1"
                    />
                    Generating unit plan...
                  </span>
                )}
                {unitPlanError && (
                  <span style={{ marginLeft: unitPlanLoading ? "0.4rem" : 0 }}>
                    {unitPlanError}
                  </span>
                )}
              </div>
            )}
            <div ref={planEndRef} />
          </div>
        </div>
      </div>
      <div
        className="plan-updates-panel"
        style={{
          position: "fixed",
          right: "1.5rem",
          bottom: "1.5rem",
          width: chatOpen ? "280px" : "52px",
          background: "#ffffff",
          borderRadius: "14px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 12px 30px rgba(15, 23, 42, 0.12)",
          padding: chatOpen ? "0.9rem" : "0.5rem",
          zIndex: 50,
          overflow: "hidden",
          transition: "width 0.2s ease",
        }}
      >
        <div className="d-flex justify-content-between align-items-center">
          {chatOpen && <div style={{ fontWeight: 600 }}>Plan updates</div>}
          <button
            type="button"
            onClick={() => setChatOpen((prev) => !prev)}
            className={`plan-updates-toggle${hasUncommittedChanges ? " has-changes" : ""}`}
            style={{
              border: "1px solid #e2e8f0",
              background: hasUncommittedChanges ? "#fef3c7" : "#f8fafc",
              borderRadius: "999px",
              width: "36px",
              height: "36px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: hasUncommittedChanges ? "#92400e" : "#475569",
              cursor: "pointer",
              position: "relative",
            }}
            aria-label={chatOpen ? "Close updates panel" : "Open updates panel"}
          >
            <i
              className={chatOpen ? "fa fa-book-open" : "fa fa-book"}
              aria-hidden="true"
            />
            {hasUncommittedChanges && !chatOpen && (
              <span
                style={{
                  position: "absolute",
                  top: "-4px",
                  right: "-4px",
                  width: "12px",
                  height: "12px",
                  background: "#f59e0b",
                  border: "2px solid #ffffff",
                  borderRadius: "50%",
                  animation: "pulse 2s infinite",
                }}
              />
            )}
          </button>
        </div>
        {chatOpen && (
          <>
            {hasUncommittedChanges && changedSectionTitles.size > 0 && (
              <div
                className="plan-updates-changes mb-2"
                style={{
                  background: "#fef3c7",
                  border: "1px solid #fbbf24",
                  borderRadius: "8px",
                  padding: "0.5rem 0.65rem",
                  fontSize: "0.85rem",
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    color: "#92400e",
                    marginBottom: "0.35rem",
                  }}
                >
                  Changes Detected
                </div>
                <div
                  className="plan-updates-changes-copy"
                  style={{ color: "#78350f", marginBottom: "0.25rem" }}
                >
                  Click to jump to changed section
                  {changedSectionTitles.size > 1 ? "s" : ""}:
                </div>
                <div className="d-flex flex-wrap gap-1">
                  {Array.from(changedSectionTitles).map((title) => (
                    <button
                      className="plan-updates-change-chip"
                      key={title}
                      type="button"
                      onClick={() => {
                        scrollToSection(title);
                        setChatOpen(false);
                      }}
                      style={{
                        border: "1px solid #f59e0b",
                        background: "#ffffff",
                        color: "#92400e",
                        borderRadius: "6px",
                        padding: "0.2rem 0.5rem",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#f59e0b";
                        e.currentTarget.style.color = "#ffffff";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#ffffff";
                        e.currentTarget.style.color = "#92400e";
                      }}
                    >
                      {title}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="text-muted small mb-2">
              {hasUncommittedChanges
                ? "Regenerate all sections to maintain coherence with your edits."
                : "Share changes and regenerate the plan."}
            </div>
            <textarea
              className="plan-updates-input"
              value={chatDraft}
              onChange={(event) => setChatDraft(event.target.value)}
              rows={4}
              placeholder={
                hasUncommittedChanges
                  ? "Add instructions (optional)..."
                  : "Tell me what to update..."
              }
              style={{
                width: "100%",
                borderRadius: "10px",
                border: "1px solid #e2e8f0",
                padding: "0.55rem 0.65rem",
                marginBottom: "0.5rem",
              }}
            />
            {rebalanceError && (
              <div className="text-danger small mb-2">{rebalanceError}</div>
            )}
            <Button
              className="plan-updates-submit"
              size="sm"
              color={hasUncommittedChanges ? "warning" : "success"}
              block
              onClick={() => handleRebalance(chatDraft)}
              disabled={rebalanceLoading}
            >
              {rebalanceLoading
                ? "Regenerating..."
                : hasUncommittedChanges
                  ? "Regenerate All Sections"
                  : "Update Plan"}
            </Button>
          </>
        )}
      </div>
    </>
  );
};

export default LessonPlanDisplay;
